// Routed clinic-directory page (M2). Loads the clinic list once, then keeps it fresh off
// the clinic.changed SSE — mirroring CasesPage's load / live-refresh / queued-mutate shape.
// The active toggle posts through the offline queue: a network drop parks the write and the
// board reconciles when the clinic.changed event lands (our own replay or another user's edit).
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { type Clinic, CLIENT_TYPE_LABELS } from '@alignova/shared';
import { api, isDeferred } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, Card, PageHeader, Pill } from '../../ui/primitives.tsx';
import { ClinicModal } from './ClinicModal.tsx';

export function ClinicsPage() {
  const can = useCan();
  const manage = can('clinic:manage');
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<Clinic | null>(null);
  // Which half of the editor to open on: the whole clinic, or just its portal credentials.
  const [modalFocus, setModalFocus] = useState<'all' | 'portal'>('all');

  const load = useCallback((search: string) => {
    const q = search.trim();
    const path = q ? `/clinics?search=${encodeURIComponent(q)}` : '/clinics';
    api
      .get<Clinic[]>(path)
      .then(setClinics)
      .catch(() => setError(t('clinics.loadError')));
  }, []);

  // Mount-only: search reloads are driven by the input below (load is a stable useCallback).
  useEffect(() => {
    load(query);
  }, [load]);

  // A clinic was created, edited, or (de)activated somewhere — pull fresh truth, keeping
  // the current search. This is the SSE half that keeps every open tab in sync.
  useLiveEvent('clinic.changed', () => {
    setQueued(false);
    load(query);
  });

  function openCreate() {
    setEditing(null);
    setShowModal(true);
  }

  function openEdit(clinic: Clinic) {
    setEditing(clinic);
    setModalFocus('all');
    setShowModal(true);
  }

  /** Straight to the login panel — grant, reset the password, enable or disable. */
  function openPortal(clinic: Clinic) {
    setEditing(clinic);
    setModalFocus('portal');
    setShowModal(true);
  }

  // Flip active state through the offline queue; the clinic.changed refresh reconciles the
  // row, so we don't mutate locally here.
  async function toggleActive(clinic: Clinic) {
    setError(null);
    const action = clinic.isActive ? 'deactivate' : 'activate';
    try {
      const res = await api.post<Clinic>(`/clinics/${clinic.id}/${action}`, {}, { queue: true });
      if (isDeferred(res)) setQueued(true);
    } catch {
      setError(t('clinics.toggleError'));
      load(query);
    }
  }

  return (
    <div>
      <PageHeader title={t('clinics.title')} subtitle={t('clinics.subtitle')} />

      <div className="mb-4 flex items-center gap-2">
        <form
          className="flex-1"
          onSubmit={(e) => {
            e.preventDefault();
            load(query);
          }}
        >
          <input
            type="search"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              load(e.target.value);
            }}
            placeholder={t('clinics.searchPlaceholder')}
            aria-label={t('clinics.searchPlaceholder')}
            className="w-full max-w-sm rounded border border-line bg-surface px-3 py-2 text-sm"
          />
        </form>
        {manage && (
          <Button className="ml-auto" onClick={openCreate}>
            {t('clinics.new')}
          </Button>
        )}
      </div>

      {queued && (
        <div className="mb-3 rounded border border-line bg-surface-2 px-3 py-2 text-xs text-muted">
          {t('sync.queued')}
        </div>
      )}
      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}

      <Card className="overflow-x-auto p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              <th className="px-4 py-3 font-medium">{t('clinics.col.code')}</th>
              <th className="px-4 py-3 font-medium">{t('clinics.col.name')}</th>
              <th className="px-4 py-3 font-medium">{t('clinics.col.type')}</th>
              <th className="px-4 py-3 font-medium">{t('clinics.col.city')}</th>
              <th className="px-4 py-3 font-medium">{t('clinics.col.status')}</th>
              <th className="px-4 py-3 font-medium">{t('clinics.col.login')}</th>
              {manage && <th className="px-4 py-3 font-medium">{t('clinics.col.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {clinics.length === 0 && (
              <tr>
                <td colSpan={manage ? 7 : 6} className="px-4 py-6 text-center text-muted">
                  {t('clinics.empty')}
                </td>
              </tr>
            )}
            {clinics.map((c) => (
              <tr key={c.id} className="border-b border-line hover:bg-surface-2">
                <td className="px-4 py-2 font-mono text-xs">{c.clinicCode}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    {manage && (
                      <button
                        type="button"
                        onClick={() => openPortal(c)}
                        title={t('clinics.portal.section')}
                        className="tap shrink-0 rounded-md border border-accent px-2 py-0.5 text-[11px] font-semibold text-accent hover:bg-accent/10"
                      >
                        {t('clinics.portal.button')}
                      </button>
                    )}
                    {/* The clinic's name opens its cases: the question staff actually have when they
                        look a clinic up is "what work have they sent us?". */}
                    <Link
                      to={`/clinics/${c.id}/cases`}
                      className="min-w-0 truncate text-accent hover:underline"
                      title="See this clinic's cases"
                    >
                      {c.name}
                    </Link>
                  </div>
                </td>
                <td className="px-4 py-2">
                  <Pill tone="neutral">{CLIENT_TYPE_LABELS[c.clientType]}</Pill>
                </td>
                <td className="px-4 py-2">{c.city ?? '-'}</td>
                <td className="px-4 py-2">
                  <Pill tone={c.isActive ? 'good' : 'neutral'}>
                    {c.isActive ? t('clinics.status.active') : t('clinics.status.inactive')}
                  </Pill>
                </td>
                <td className="px-4 py-2">
                  {!c.portal ? (
                    <Pill tone="neutral">{t('clinics.portal.none')}</Pill>
                  ) : c.portal.active ? (
                    <Pill tone="good">{t('clinics.portal.active')}</Pill>
                  ) : (
                    <Pill tone="bad">{t('clinics.portal.disabled')}</Pill>
                  )}
                </td>
                {manage && (
                  <td className="px-4 py-2">
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={() => openEdit(c)}>
                        {t('clinics.edit')}
                      </Button>
                      <Button variant="ghost" onClick={() => toggleActive(c)}>
                        {c.isActive ? t('clinics.deactivate') : t('clinics.activate')}
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <ClinicModal
        open={showModal}
        clinic={editing}
        focus={modalFocus}
        onClose={() => setShowModal(false)}
        onSaved={() => load(query)}
      />
    </div>
  );
}
